// SagaPay Checkout - Main App v1.0.1
// Inline checkout widget for e-commerce integration
// Direct access protection enabled

import { useEffect, useState } from 'react';
import { useCheckoutStore } from './store/checkout';
import { ThemeProvider } from './store/theme';
import { getStoreDetails, getMerchantPaymentMethods, getSession, getPaymentMethods } from './api/client';
import { IdentityStep } from './steps/IdentityStep';
import { AddressStep } from './steps/AddressStep';
import { ShippingStep } from './steps/ShippingStep';
import { PaymentStep } from './steps/PaymentStep';
import { OrderSummary } from './components/OrderSummary';
import type { CheckoutConfig } from './types/config';
import './styles/global.css';
import styles from './App.module.css';

// Global config from parent
declare global {
  interface Window {
    SagaPayCheckoutConfig?: CheckoutConfig;
  }
}

// Helper to notify parent window/callbacks
function notifyParent(type: string, data: unknown) {
  // Send to parent window (for iframe)
  if (window.parent !== window) {
    window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, '*');
  }
  
  // Dispatch custom event (for inline widget)
  window.dispatchEvent(new CustomEvent(`sagapay:${type.toLowerCase()}`, { detail: data }));
}

function CheckoutContent() {
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const initSession = useCheckoutStore((s) => s.initSession);
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setSurfboardData = useCheckoutStore((s) => s.setSurfboardData);
  const setCustomShippingOptions = useCheckoutStore((s) => s.setCustomShippingOptions);
  const setUseBuiltInShipping = useCheckoutStore((s) => s.setUseBuiltInShipping);
  const setSkipShipping = useCheckoutStore((s) => s.setSkipShipping);
  const setTheme = useCheckoutStore((s) => s.setTheme);
  const theme = useCheckoutStore((s) => s.theme);
  const merchant = useCheckoutStore((s) => s.merchant);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [missingConfig, setMissingConfig] = useState(false);

  // Parse initialization config from various sources
  const getCheckoutConfig = (): Partial<CheckoutConfig> | null => {
    // 1. Check for global config (set by SDK)
    if (window.SagaPayCheckoutConfig) {
      return window.SagaPayCheckoutConfig;
    }

    // 2. Check URL path for session ID (e.g., /sp_sess_xxx)
    const pathMatch = window.location.pathname.match(/^\/(sp_sess_[a-zA-Z0-9_]+)/);
    if (pathMatch) {
      const sessionId = pathMatch[1];
      return {
        sessionId,
        partnerId: 'sagapay',
        merchantId: 'from-session', // Will be loaded from session API
        storeId: 'from-session',    // Will be loaded from session API
      };
    }

    // 3. Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session') || params.get('sessionId');
    
    // If we have a session ID, we'll fetch the rest from API
    if (sessionId) {
      return {
        sessionId,
        partnerId: params.get('partnerId') || 'sagapay',
        merchantId: params.get('merchantId') || '',
        storeId: params.get('storeId') || '',
      };
    }

    // 4. Check for base64 encoded config in URL
    const configParam = params.get('config');
    if (configParam) {
      try {
        return JSON.parse(atob(configParam));
      } catch (e) {
        console.error('Failed to parse config:', e);
      }
    }

    // 5. Check if embedded mode (loaded via iframe from SDK)
    const embedded = params.get('embedded');
    if (embedded === 'true') {
      // Wait for SDK to send config via postMessage
      return null;
    }

    // 6. No valid config - this is a direct access without SDK
    return null;
  };

  // Listen for PostMessage config from SDK
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle config from SDK
      if (event.data?.type === 'SAGAPAY_CONFIG') {
        const payload = event.data.payload;
        
        // Apply theme
        if (payload.theme) {
          setTheme(payload.theme);
        }
        
        // Apply custom shipping
        if (payload.customShipping) {
          setCustomShippingOptions(payload.customShipping);
          setUseBuiltInShipping(false);
        }
        
        // Apply customer data
        if (payload.customer) {
          setCustomer({
            email: payload.customer.email || '',
            name: `${payload.customer.firstName || ''} ${payload.customer.lastName || ''}`.trim(),
            phone: payload.customer.phone || '',
          });
        }
      }
      
      // Handle legacy config format
      if (event.data?.type === 'SAGAPAY_CHECKOUT_CONFIG') {
        window.SagaPayCheckoutConfig = event.data.config;
        // Trigger re-initialization
        initializeCheckout();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Initialize checkout
  const initializeCheckout = async () => {
    setIsInitializing(true);
    setInitError(null);
    setMissingConfig(false);

    try {
      const config = getCheckoutConfig();
      
      // No config provided - show error
      if (!config) {
        setMissingConfig(true);
        setIsInitializing(false);
        return;
      }
      
      const sessionId = config.sessionId;
      let merchantId = config.merchantId;
      let storeId = config.storeId;
      let cartData = config.cart;
      let customerData = config.customer;

      // Validate sessionId is present
      if (!sessionId) {
        setInitError('Mangler session ID. Bruk SagaPay SDK for å initiere checkout.');
        setIsInitializing(false);
        return;
      }

      // Fetch session data from API (single call for all data)
      try {
        console.log('[Checkout] Fetching session:', sessionId);
        const sessionResponse = await getSession(sessionId);
        console.log('[Checkout] Session response:', sessionResponse);
        
        if (sessionResponse.error) {
          console.error('[Checkout] Session error:', sessionResponse.error);
          setInitError(sessionResponse.error.message || 'Kunne ikke hente checkout-sesjon');
          setIsInitializing(false);
          return;
        }
        
        if (sessionResponse.data) {
          const sessionData = sessionResponse.data as any;
          console.log('[Checkout] Session data:', sessionData);
          
          // Get merchantId/storeId from session
          merchantId = sessionData.merchantId || merchantId;
          storeId = sessionData.storeId || sessionData.merchantId || storeId;
          
          // Map cart data from API format to UI format
          if (sessionData.cart) {
            const apiCart = sessionData.cart;
            console.log('[Checkout] API cart:', apiCart);
            
            cartData = {
              items: (apiCart.orderLines || apiCart.items || []).map((line: any) => ({
                id: line.id,
                name: line.name,
                quantity: line.quantity,
                unitPrice: {
                  amount: line.unitPrice?.value ?? line.unitPrice?.amount ?? line.unitPrice ?? 0,
                  currency: line.unitPrice?.currency ?? apiCart.totalAmount?.currency ?? 'NOK',
                },
                imageUrl: line.imageUrl,
              })),
              totalAmount: {
                amount: apiCart.totalAmount?.value ?? apiCart.totalAmount?.amount ?? 0,
                currency: apiCart.totalAmount?.currency ?? 'NOK',
              },
              totalItems: (apiCart.orderLines || apiCart.items || []).reduce(
                (sum: number, line: any) => sum + (line.quantity || 1), 0
              ),
            };
            console.log('[Checkout] Mapped cart:', cartData);
          }
          
          if (sessionData.customer) {
            customerData = sessionData.customer;
          }
          
          // Get Surfboard SDK data from session if available
          if (sessionData.surfboardOrderId && sessionData.surfboardNonce) {
            // publicKey comes from merchant config or we fetch it separately
            const pk = sessionData.publicKey || sessionData.surfboardPublicKey;
            if (pk) {
              setSurfboardData(sessionData.surfboardOrderId, sessionData.surfboardNonce, pk);
              console.log('[Checkout] Surfboard data from session:', { 
                orderId: sessionData.surfboardOrderId, 
                hasNonce: !!sessionData.surfboardNonce,
                hasPublicKey: !!pk 
              });
            } else {
              console.log('[Checkout] Surfboard orderId/nonce found but no publicKey in session');
            }
          }
        }
      } catch (e) {
        console.error('[Checkout] Failed to fetch session:', e);
        setInitError('Kunne ikke hente checkout-sesjon. Prøv igjen.');
        setIsInitializing(false);
        return;
      }
      
      // After session fetch, validate we have data
      const validSessionId: string = sessionId;
      const validMerchantId: string = merchantId || 'unknown';

      // Validate cart data exists
      if (!cartData || !cartData.items || cartData.items.length === 0) {
        console.error('[Checkout] Cart validation failed:', cartData);
        setInitError('Handlekurv mangler. Opprett en gyldig checkout-sesjon først.');
        setIsInitializing(false);
        return;
      }

      // Initialize with cart and session data
      initSession(validSessionId, {
        cart: cartData,
        merchant: {
          merchantId: validMerchantId,
          merchantName: 'Laster...',
          features: {
            enableQuickPay: true,
            enableAddressSaving: true,
            enableVipps: true,
            collectPhone: true,
          },
        },
      });

      // Apply custom shipping options if provided
      if (config.customShipping && config.customShipping.length > 0) {
        setCustomShippingOptions(config.customShipping);
        setUseBuiltInShipping(false);
      } else if (config.useBuiltInShipping !== undefined) {
        setUseBuiltInShipping(config.useBuiltInShipping);
      }
      
      // Skip shipping for digital products
      if (config.skipShipping) {
        setSkipShipping(true);
      }
      
      // Apply theme configuration
      if (config.theme) {
        setTheme(config.theme);
      }

      // Set customer if provided
      if (customerData?.email) {
        setCustomer({
          email: customerData.email,
          name: customerData.name || '',
          phone: customerData.phone || '',
        });
      }

      // Notify parent that checkout is ready
      notifyParent('CHECKOUT_READY', { sessionId: validSessionId, step: 'identity' });

      // Fetch store details and payment methods from Surfboard API
      // Uses new endpoint that looks up storeId from database
      const [storeResponse, paymentMethodsResponse] = await Promise.all([
        getStoreDetails(validMerchantId),
        getMerchantPaymentMethods(validMerchantId),
      ]);

      // Extract activated payment methods
      const activatedMethods = paymentMethodsResponse.data?.data?.map(
        (m) => m.paymentMethod
      ) || ['CARD', 'VIPPS'];

      // Fetch Surfboard SDK data for payment initialization
      // This is done after session is created and returns orderId, nonce, publicKey
      if (sessionId && sessionId !== 'demo-session') {
        try {
          const paymentDataResponse = await getPaymentMethods(sessionId);
          if (paymentDataResponse.data) {
            const { surfboardOrderId, surfboardNonce, publicKey } = paymentDataResponse.data;
            if (surfboardOrderId && surfboardNonce && publicKey) {
              setSurfboardData(surfboardOrderId, surfboardNonce, publicKey);
              console.log('[App] Surfboard SDK data loaded:', { surfboardOrderId, publicKey: publicKey.substring(0, 20) + '...' });
            }
          }
        } catch (e) {
          console.warn('Could not fetch payment methods / Surfboard data:', e);
        }
      }

      if (storeResponse.data?.data) {
        const storeData = storeResponse.data.data;
        // Update merchant with real store info and payment methods
        initSession(validSessionId, {
          cart: cartData,
          merchant: {
            merchantId: storeData.merchantId,
            merchantName: storeData.name,
            features: {
              enableQuickPay: true,
              enableAddressSaving: true,
              enableVipps: activatedMethods.includes('VIPPS'),
              collectPhone: true,
            },
            storeInfo: {
              storeId: storeData.storeId,
              name: storeData.name,
              email: storeData.email,
              phone: storeData.phone,
              merchantWebshopURL: storeData.onlineInfo?.merchantWebshopURL,
              termsAndConditionsURL: storeData.onlineInfo?.termsAndConditionsURL,
              privacyPolicyURL: storeData.onlineInfo?.privacyPolicyURL,
            },
            activatedPaymentMethods: activatedMethods,
          },
        });
      } else {
        // Still update with payment methods even if store details failed
        initSession(validSessionId, {
          cart: cartData,
          merchant: {
            merchantId: validMerchantId,
            merchantName: 'Butikk',
            features: {
              enableQuickPay: true,
              enableAddressSaving: true,
              enableVipps: activatedMethods.includes('VIPPS'),
              collectPhone: true,
            },
            activatedPaymentMethods: activatedMethods,
          },
        });
      }

    } catch (error) {
      console.error('Failed to initialize checkout:', error);
      setInitError('Kunne ikke initialisere checkout. Vennligst prøv igjen.');
    }

    setIsInitializing(false);
  };

  useEffect(() => {
    initializeCheckout();
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 'identity':
        return <IdentityStep />;
      case 'address':
        return <AddressStep />;
      case 'shipping':
        return <ShippingStep />;
      case 'payment':
        return <PaymentStep />;
      default:
        return <IdentityStep />;
    }
  };

  // Loading state
  if (isInitializing) {
    return (
      <div className={styles.checkout}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Laster checkout...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (initError) {
    return (
      <div className={styles.checkout}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p className={styles.errorText}>{initError}</p>
          <button className={styles.retryButton} onClick={() => initializeCheckout()}>
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  // Missing config state - direct access without SDK
  if (missingConfig) {
    return (
      <div className={styles.checkout}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ff5b24' }}>
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <h2 className={styles.errorTitle}>SagaPay Checkout</h2>
          <p className={styles.errorText}>
            Denne siden kan kun åpnes via SagaPay SDK.<br/>
            Integrer checkout i din nettbutikk for å komme i gang.
          </p>
          <div className={styles.integrationHelp}>
            <p className={styles.codeLabel}>Eksempel på integrasjon:</p>
            <pre className={styles.codeBlock}>
{`<script src="https://cdn.sagapay.no/sdk/v1/sagapay.min.js"></script>
<script>
  SagaPay.checkout({
    sessionId: 'sess_xxx',
    containerId: 'checkout',
    onComplete: (result) => { /* ... */ }
  });
</script>`}
            </pre>
          </div>
          <a 
            href="https://docs.sagapay.no/checkout" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.docsLink}
          >
            Les dokumentasjonen →
          </a>
        </div>
      </div>
    );
  }

  // Get theme config for conditional rendering
  const showPoweredBy = theme?.showPoweredBy !== false;
  const showOrderSummary = theme?.showOrderSummary !== false;
  const merchantLogo = theme?.logoUrl || merchant?.logoUrl;

  return (
    <div className={styles.checkout}>
      {/* Optional merchant logo header */}
      {merchantLogo && (
        <header className={styles.merchantHeader} style={{ justifyContent: theme?.logoPosition || 'left' }}>
          <img 
            src={merchantLogo} 
            alt={merchant?.merchantName || 'Logo'} 
            className={styles.merchantLogo}
            style={{ height: theme?.logoHeight || 32 }}
          />
        </header>
      )}
      
      <main className={styles.main}>
        <div className={styles.content}>
          {renderStep()}
        </div>
      </main>
      
      {showOrderSummary && <OrderSummary />}
      
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <a 
            href={merchant?.storeInfo?.termsAndConditionsURL || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Kjøpsvilkår
          </a>
          <span>•</span>
          <a 
            href={merchant?.storeInfo?.privacyPolicyURL || '#'} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Personvernerklæring
          </a>
        </div>
        {showPoweredBy && (
          <div className={styles.poweredBy}>
            <span>Drevet av</span>
            <img 
              src="https://res.cloudinary.com/martinsurf/image/upload/v1763562569/surfboardmerchant/cexdplrnd3tr54xhjjvt.png" 
              alt="Saga" 
              className={styles.sagaLogo}
            />
          </div>
        )}
      </footer>
    </div>
  );
}

// Main App with ThemeProvider wrapper
export default function App() {
  const theme = useCheckoutStore((s) => s.theme);
  
  return (
    <ThemeProvider config={theme || undefined}>
      <CheckoutContent />
    </ThemeProvider>
  );
}